import React, { useState, useEffect } from 'react';
import './BookingCheckout.css';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase } from '../supabaseClient';

const API_BASE_URL = process.env.REACT_APP_API_GATEWAY_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function PaymentForm({ formData, onInputChange, total, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    const birthDate = new Date(formData.birthDate);
    const age = Math.floor((Date.now() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
    if (age < 18) {
      alert('You must be 18 or older to make a booking.');
      return;
    }

    if (!formData.termsAgreed || !formData.ageVerified || !formData.risksAcknowledged) {
      alert('Please accept all terms and agreements to continue.');
      return;
    }

    setProcessing(true);
    setPaymentError(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (error) {
      setPaymentError(error.message || 'Payment failed. Please check your card details and try again.');
      setProcessing(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess();
    } else {
      setPaymentError('Payment did not complete. Please try again.');
    }
    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Full Name</label>
        <input
          type="text"
          name="fullName"
          placeholder="Enter your full name"
          value={formData.fullName}
          onChange={onInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label>Email Address</label>
        <input
          type="email"
          name="email"
          placeholder="your@email.com"
          value={formData.email}
          onChange={onInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label>Phone Number</label>
        <input
          type="tel"
          name="phone"
          placeholder="+1 (555) 123-4567"
          value={formData.phone}
          onChange={onInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label>Date of Birth (Age Verification)</label>
        <input
          type="date"
          name="birthDate"
          value={formData.birthDate}
          onChange={onInputChange}
          required
        />
        <div style={{ fontSize: '0.8rem', opacity: 0.8, marginTop: '0.3rem' }}>
          Must be 18+ to book. Some activities have age restrictions.
        </div>
      </div>

      {/* Legal Agreements */}
      <div style={{ background: 'rgba(255,107,107,0.1)', padding: '1.5rem', borderRadius: '15px', margin: '1.5rem 0', border: '1px solid rgba(255,107,107,0.3)' }}>
        <h3 style={{ marginBottom: '1rem', color: '#ff6b6b' }}>⚠️ Important Travel Disclaimers</h3>
        <div style={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
          <p><strong>Health & Safety:</strong> Travel involves inherent risks. Consult your doctor before travel. Some activities may be physically demanding.</p>
          <p><strong>Travel Requirements:</strong> Valid passport required for international travel. Visas may be required. Check government travel advisories.</p>
          <p><strong>Insurance:</strong> Travel insurance strongly recommended. AspireAI is not liable for medical emergencies, trip cancellations, or lost belongings.</p>
          <p><strong>Third-Party Services:</strong> We are a booking platform. Actual services provided by third-party partners.</p>
        </div>
      </div>

      <div style={{ margin: '1rem 0' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', lineHeight: 1.1, fontSize: '0.7rem', marginBottom: '0.5rem' }}>
          <input
            type="checkbox"
            name="termsAgreed"
            checked={formData.termsAgreed}
            onChange={onInputChange}
            required
            style={{ marginRight: '0.5rem', marginTop: '0.1rem' }}
          />
          <span>I agree to the Terms of Service and Privacy Policy</span>
        </label>
      </div>

      <div style={{ margin: '1rem 0' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', lineHeight: 1.1, fontSize: '0.7rem', marginBottom: '0.5rem' }}>
          <input
            type="checkbox"
            name="ageVerified"
            checked={formData.ageVerified}
            onChange={onInputChange}
            required
            style={{ marginRight: '0.5rem', marginTop: '0.1rem' }}
          />
          <span>I confirm I am 18+ years old and authorized to make this booking</span>
        </label>
      </div>

      <div style={{ margin: '1rem 0' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', lineHeight: 1.1, fontSize: '0.7rem', marginBottom: '0.5rem' }}>
          <input
            type="checkbox"
            name="risksAcknowledged"
            checked={formData.risksAcknowledged}
            onChange={onInputChange}
            required
            style={{ marginRight: '0.5rem', marginTop: '0.1rem' }}
          />
          <span>I acknowledge travel risks and have read all disclaimers above</span>
        </label>
      </div>

      <div style={{ margin: '1.5rem 0' }}>
        <PaymentElement />
      </div>

      {paymentError && (
        <div style={{ color: '#ff6b6b', marginBottom: '1rem', fontSize: '0.9rem' }}>{paymentError}</div>
      )}

      <button
        type="submit"
        className="book-now-btn"
        disabled={!stripe || processing}
      >
        {processing ? 'Processing...' : `🚀 Complete Journey Only Booking - $${total}`}
      </button>
      <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.85rem', opacity: 0.8 }}>
        ✈️ Flights booked separately above
      </div>

      <div className="security-badges">
        <span>🔒 SSL Secured</span>
        <span>💳 Stripe Protected</span>
        <span>🛡️ 24h Free Cancellation</span>
      </div>
    </form>
  );
}

const BookingCheckout = () => {
  const [journeyTitle, setJourneyTitle] = useState('🤖 AI Recommended: Spiritual Experience in India');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    birthDate: '',
    departureDate: '',
    returnDate: '',
    travelers: '1',
    termsAgreed: false,
    ageVerified: false,
    risksAcknowledged: false
  });
  // Static placeholder pricing - real per-journey pricing isn't wired into
  // checkout yet, only the payment collection itself is now real.
  const [pricing] = useState({
    accommodation: 1200,
    experiences: 0,
    serviceFee: 0,
    flights: 650
  });
  const [clientSecret, setClientSecret] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const journey = urlParams.get('journey');
    if (journey) {
      setJourneyTitle(decodeURIComponent(journey));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculateTotal = () => {
    return pricing.accommodation + pricing.experiences + pricing.serviceFee;
  };

  const total = calculateTotal();

  useEffect(() => {
    async function createPaymentIntent() {
      try {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (!token) {
          setLoadError('You must be signed in to complete a booking.');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/create-payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            amount: Math.round(total * 100),
            currency: 'usd',
            metadata: { journeyTitle }
          })
        });
        const body = await response.json();
        if (!response.ok || !body.clientSecret) {
          throw new Error(body.error || 'Could not start payment');
        }
        setClientSecret(body.clientSecret);
      } catch (error) {
        console.error('Error creating payment intent:', error);
        setLoadError('Could not initialize secure payment right now. Please try again.');
      }
    }
    createPaymentIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  if (bookingConfirmed) {
    return (
      <div className="checkout-container">
        <div className="payment-section" style={{ textAlign: 'center', padding: '4rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <h2>Booking Confirmed!</h2>
          <p>Your payment for {journeyTitle} was successful.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div className="booking-details">
        <div className="journey-summary">
          <h1>{journeyTitle}</h1>
          <img
            src="https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=600"
            alt="Journey destination"
            className="journey-image"
          />
          <p>A transformative journey combining spiritual practices, cultural immersion, and personal growth activities.</p>
        </div>

        <div className="accommodation-preview">
          <h3>🏨 Your Accommodation</h3>
          <div className="accommodation-item">
            <div>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Ananda in the Himalayas</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '0.5rem' }}>
                ⭐ 4.9 • Luxury Wellness Resort • Rishikesh, Uttarakhand
              </div>
              <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                Award-winning luxury wellness resort nestled in the Himalayan foothills.
              </div>
            </div>
          </div>
        </div>

        <div className="experiences-list">
          <h3>🎯 Included Experiences</h3>
          <div className="experience-item">
            <div>
              <div style={{ fontWeight: 600 }}>Transformative Experiences</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>7 days • ⭐ 4.9</div>
            </div>
            <div style={{ fontWeight: 600 }}>Included</div>
          </div>
          <div className="experience-item">
            <div>
              <div style={{ fontWeight: 600 }}>Accommodation & Activities</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>Full journey • ⭐ 4.8</div>
            </div>
            <div style={{ fontWeight: 600 }}>Included</div>
          </div>
        </div>

        <div className="travel-details">
          <h3>✈️ Travel Information</h3>
          <div className="form-group">
            <label>Travel Dates</label>
            <input
              type="date"
              name="departureDate"
              value={formData.departureDate}
              onChange={handleInputChange}
              required
            />
            <input
              type="date"
              name="returnDate"
              value={formData.returnDate}
              onChange={handleInputChange}
              required
              style={{ marginTop: '0.5rem' }}
            />
          </div>
          <div className="form-group">
            <label>Number of Travelers</label>
            <select
              name="travelers"
              value={formData.travelers}
              onChange={handleInputChange}
            >
              <option value="1">1 Traveler</option>
              <option value="2">2 Travelers</option>
              <option value="3">3 Travelers</option>
              <option value="4">4 Travelers</option>
            </select>
          </div>
        </div>
      </div>

      <div className="payment-section">
        <h2>💳 Complete Your Booking</h2>

        <div className="price-breakdown">
          <div className="price-row">
            <span>🏨 Accommodation (7 nights)</span>
            <span>${pricing.accommodation}</span>
          </div>
          <div className="price-row">
            <span>Experiences</span>
            <span>${pricing.experiences}</span>
          </div>
          <div className="price-row">
            <span>AspireAI Service Fee</span>
            <span>${pricing.serviceFee}</span>
          </div>
          <div className="price-row total-price">
            <span><strong>Total Journey Package</strong></span>
            <span>${total}</span>
          </div>
          <div style={{ fontSize: '0.85rem', opacity: 0.8, marginTop: '0.5rem', textAlign: 'center' }}>
            ✈️ Flights booked separately • 🎯 Journey experiences included
          </div>
        </div>

        {loadError && (
          <div style={{ color: '#ff6b6b', textAlign: 'center', padding: '2rem' }}>{loadError}</div>
        )}

        {!clientSecret && !loadError && (
          <div style={{ textAlign: 'center', padding: '2rem', opacity: 0.8 }}>Preparing secure payment…</div>
        )}

        {clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              formData={formData}
              onInputChange={handleInputChange}
              total={total}
              onSuccess={() => setBookingConfirmed(true)}
            />
          </Elements>
        )}
      </div>
    </div>
  );
};

export default BookingCheckout;
